import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2,
  CalendarRange,
  ChevronDown,
  FileDown,
  FileText,
  RefreshCw,
  Search,
  Shield,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ScrollArea } from './ui/scroll-area';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { AdminPageHeader, adminPageStackClass } from './AdminChrome';

const DEFAULT_EXPORT_SECTIONS = {
  include_cover: true,
  include_exec_summary: true,
  include_scans_summary: true,
  include_scans_by_day: true,
  include_scans_gps_stats: true,
  include_quality_summary: true,
  include_quality_distribution: true,
  include_quality_by_form: true,
  include_weakest_criteria: true,
  include_validated_visits_table: true,
  include_alerts: true,
};

const CompteRenduPage = ({ token, apiBase, theme, onUnauthorized }) => {
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [selectedEntrepriseId, setSelectedEntrepriseId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [enterprisePopoverOpen, setEnterprisePopoverOpen] = useState(false);
  const [enterpriseSearch, setEnterpriseSearch] = useState('');
  const [exportSections, setExportSections] = useState(() => ({ ...DEFAULT_EXPORT_SECTIONS }));
  const [introText, setIntroText] = useState('');
  const [commentScan, setCommentScan] = useState('');
  const [commentQuality, setCommentQuality] = useState('');
  const [conclusionText, setConclusionText] = useState('');
  const [signatureName, setSignatureName] = useState('');
  const [signatureRole, setSignatureRole] = useState('');
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingXlsx, setExportingXlsx] = useState(false);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const handleApiError = useCallback(
    (error) => {
      if (error.response?.status === 401) {
        onUnauthorized?.();
        return;
      }
      const msg =
        error.response?.data?.detail || error.message || 'Impossible de charger les données';
      toast.error(typeof msg === 'string' ? msg : 'Erreur serveur');
    },
    [onUnauthorized]
  );

  const loadSites = useCallback(async () => {
    setSitesLoading(true);
    try {
      const { data } = await axios.get(`${apiBase}/admin/sites`, { headers: authHeaders });
      const list = Array.isArray(data) ? data : [];
      setSites(
        list
          .map((s) => ({
            id: String(s.id ?? s._id ?? ''),
            nom: (s.nom || s.name || '').trim() || '—',
          }))
          .filter((s) => s.id)
      );
    } catch (e) {
      handleApiError(e);
      setSites([]);
    } finally {
      setSitesLoading(false);
    }
  }, [apiBase, authHeaders, handleApiError]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  const selectedEntrepriseNom = useMemo(() => {
    if (!selectedEntrepriseId) return '';
    return sites.find((s) => s.id === selectedEntrepriseId)?.nom || '';
  }, [sites, selectedEntrepriseId]);

  const filteredSites = useMemo(() => {
    const q = enterpriseSearch.trim().toLowerCase();
    if (!q) return sites;
    return sites.filter((s) => s.nom.toLowerCase().includes(q));
  }, [sites, enterpriseSearch]);

  const toggleExportSection = (key) => {
    setExportSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const downloadExport = async (format) => {
    if (!selectedEntrepriseId) {
      toast.error('Choisissez une entreprise pour l’export destiné au client.');
      return;
    }
    const setBusy = format === 'pdf' ? setExportingPdf : setExportingXlsx;
    setBusy(true);
    try {
      const { data, headers } = await axios.post(
        `${apiBase}/admin/compte-rendu/export`,
        {
          format,
          date_from: dateFrom || null,
          date_to: dateTo || null,
          entreprise_id: selectedEntrepriseId,
          sections: exportSections,
          intro_text: introText.trim() || null,
          comment_scan: commentScan.trim() || null,
          comment_quality: commentQuality.trim() || null,
          conclusion_text: conclusionText.trim() || null,
          signature_name: signatureName.trim() || null,
          signature_role: signatureRole.trim() || null,
        },
        {
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          responseType: 'blob',
        }
      );
      const cd = headers['content-disposition'] || '';
      const star = cd.match(/filename\*=UTF-8''([^;]+)/);
      const plain = cd.match(/filename="([^"]+)"/);
      let fname = `compte-rendu.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      if (star?.[1]) {
        try {
          fname = decodeURIComponent(star[1].trim());
        } catch {
          fname = star[1];
        }
      } else if (plain?.[1]) {
        fname = plain[1];
      }
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export généré');
    } catch (e) {
      if (e.response?.status === 401) {
        onUnauthorized?.();
        return;
      }
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const j = JSON.parse(text);
          toast.error(typeof j.detail === 'string' ? j.detail : 'Erreur lors de l’export');
        } catch {
          toast.error('Erreur lors de l’export');
        }
        return;
      }
      handleApiError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={adminPageStackClass}>
      <AdminPageHeader
        theme={theme}
        icon={FileText}
        title="Compte rendu"
        subtitle={
          <>
            Génération de documents PDF ou Excel pour <strong>une entreprise</strong> à la fois : indicateurs agrégés
            (RGPD), sans ventilation ni comparaison entre établissements.
          </>
        }
        footer={
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-2.5 shrink-0 justify-end">
            <div className="flex items-center gap-1.5 flex-wrap">
              <CalendarRange className="h-3.5 w-3.5 shrink-0 hidden sm:block" style={{ color: theme.accent }} />
              <div className="flex items-center gap-1.5">
                <Label
                  htmlFor="cr-df"
                  className="text-[11px] sr-only sm:not-sr-only sm:w-5 shrink-0"
                  style={{ color: theme.textSecondary }}
                >
                  Du
                </Label>
                <Input
                  id="cr-df"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="h-9 w-[10.5rem] sm:w-[11rem] text-sm rounded-md"
                  style={{ borderColor: theme.border, color: theme.text, backgroundColor: theme.surface }}
                />
              </div>
              <span className="text-xs shrink-0" style={{ color: theme.textSecondary }}>
                au
              </span>
              <div className="flex items-center gap-1.5">
                <Label htmlFor="cr-dt" className="text-[11px] sr-only" style={{ color: theme.textSecondary }}>
                  Au
                </Label>
                <Input
                  id="cr-dt"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="h-9 w-[10.5rem] sm:w-[11rem] text-sm rounded-md"
                  style={{ borderColor: theme.border, color: theme.text, backgroundColor: theme.surface }}
                />
              </div>
            </div>

            <Popover
              open={enterprisePopoverOpen}
              onOpenChange={(open) => {
                setEnterprisePopoverOpen(open);
                if (!open) setEnterpriseSearch('');
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9 gap-1.5 rounded-md justify-between min-w-[10rem] sm:min-w-[12rem]"
                  style={{ borderColor: theme.border, color: theme.text, backgroundColor: theme.background }}
                >
                  <span className="flex items-center gap-1.5 min-w-0">
                    <Building2 className="h-3.5 w-3.5 shrink-0 opacity-80" />
                    <span className="truncate text-sm font-medium text-left">
                      {selectedEntrepriseNom || 'Entreprise'}
                    </span>
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-[min(100vw-2rem,22rem)] p-0 overflow-hidden"
                align="end"
                sideOffset={6}
                style={{ borderColor: theme.border, backgroundColor: theme.surface }}
              >
                <div className="p-3 border-b space-y-2" style={{ borderColor: theme.border }}>
                  <p className="text-sm font-semibold" style={{ color: theme.text }}>
                    Entreprise du compte rendu
                  </p>
                  <div className="relative">
                    <Search
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 opacity-45"
                      style={{ color: theme.textSecondary }}
                    />
                    <Input
                      type="search"
                      placeholder="Rechercher…"
                      value={enterpriseSearch}
                      onChange={(e) => setEnterpriseSearch(e.target.value)}
                      className="h-8 pl-8 text-sm rounded-md"
                      style={{ borderColor: theme.border, color: theme.text, backgroundColor: theme.background }}
                    />
                  </div>
                </div>
                <ScrollArea className="h-[min(14rem,40vh)]">
                  <div className="p-3">
                    {sitesLoading ? (
                      <p className="text-xs text-center py-4" style={{ color: theme.textSecondary }}>
                        Chargement…
                      </p>
                    ) : sites.length === 0 ? (
                      <p className="text-xs text-center py-4" style={{ color: theme.textSecondary }}>
                        Aucune entreprise.
                      </p>
                    ) : filteredSites.length === 0 ? (
                      <p className="text-xs text-center py-4" style={{ color: theme.textSecondary }}>
                        Aucun résultat.
                      </p>
                    ) : (
                      <RadioGroup value={selectedEntrepriseId} onValueChange={setSelectedEntrepriseId}>
                        <div className="space-y-2">
                          {filteredSites.map((s) => (
                            <div
                              key={s.id}
                              className="flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-black/[0.04] dark:hover:bg-white/[0.06]"
                              style={{ color: theme.text }}
                            >
                              <RadioGroupItem value={s.id} id={`cr-ent-${s.id}`} />
                              <Label
                                htmlFor={`cr-ent-${s.id}`}
                                className="text-sm truncate flex-1 cursor-pointer font-normal"
                                title={s.nom}
                              >
                                {s.nom}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </RadioGroup>
                    )}
                  </div>
                </ScrollArea>
              </PopoverContent>
            </Popover>
          </div>
        }
      />

      <Card className="rounded-2xl border shadow-sm" style={{ backgroundColor: theme.surface, borderColor: theme.border }}>
        <CardHeader className="p-4 pb-2 pt-3.5 space-y-0">
          <CardTitle
            className="text-sm sm:text-base font-semibold flex items-center gap-2 flex-wrap"
            style={{ color: theme.text }}
          >
            <Shield className="h-4 w-4 shrink-0" style={{ color: theme.accent }} />
            Export client (PDF / Excel)
            <Badge variant="outline" className="text-[10px] font-normal">
              RGPD — données minimisées
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0 space-y-4">
          <p className="text-xs leading-relaxed" style={{ color: theme.textSecondary }}>
            PDF au graphisme <strong>Horizon Solution</strong> (logo, couleurs), pour l’entreprise sélectionnée uniquement
            (aucune ventilation ni comparaison entre sites ou établissements). Pas de noms de salariés ni coordonnées GPS
            précises ; agrégats et contrôles qualité <strong>validés</strong> uniquement. Mentions RGPD en fin de document.
          </p>
          {!selectedEntrepriseId ? (
            <p className="text-sm rounded-md border px-3 py-2" style={{ borderColor: theme.border, color: theme.text }}>
              Choisissez <strong>une entreprise</strong> dans le menu ci-dessus pour activer le téléchargement.
            </p>
          ) : null}
          <div className="space-y-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: theme.textSecondary }}>
              Textes (PDF / Excel — feuille Synthèse)
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs" style={{ color: theme.textSecondary }}>
                  Introduction
                </Label>
                <Textarea
                  value={introText}
                  onChange={(e) => setIntroText(e.target.value)}
                  placeholder="Contexte, message d’ouverture…"
                  rows={3}
                  className="text-sm min-h-[4.5rem]"
                  style={{ borderColor: theme.border, color: theme.text, backgroundColor: theme.background }}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs" style={{ color: theme.textSecondary }}>
                  Commentaire — bloc Scan
                </Label>
                <Textarea
                  value={commentScan}
                  onChange={(e) => setCommentScan(e.target.value)}
                  placeholder="Interprétation de l’activité des scans…"
                  rows={3}
                  className="text-sm min-h-[4.5rem]"
                  style={{ borderColor: theme.border, color: theme.text, backgroundColor: theme.background }}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs" style={{ color: theme.textSecondary }}>
                  Commentaire — bloc Contrôle qualité
                </Label>
                <Textarea
                  value={commentQuality}
                  onChange={(e) => setCommentQuality(e.target.value)}
                  placeholder="Lecture des résultats CQ…"
                  rows={3}
                  className="text-sm min-h-[4.5rem]"
                  style={{ borderColor: theme.border, color: theme.text, backgroundColor: theme.background }}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs" style={{ color: theme.textSecondary }}>
                  Conclusion
                </Label>
                <Textarea
                  value={conclusionText}
                  onChange={(e) => setConclusionText(e.target.value)}
                  placeholder="Prochaines étapes, synthèse…"
                  rows={3}
                  className="text-sm min-h-[4.5rem]"
                  style={{ borderColor: theme.border, color: theme.text, backgroundColor: theme.background }}
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2 grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs" style={{ color: theme.textSecondary }}>
                    Nom du signataire
                  </Label>
                  <Input
                    value={signatureName}
                    onChange={(e) => setSignatureName(e.target.value)}
                    placeholder="Prénom Nom"
                    className="h-9 text-sm"
                    style={{ borderColor: theme.border, color: theme.text, backgroundColor: theme.background }}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs" style={{ color: theme.textSecondary }}>
                    Fonction
                  </Label>
                  <Input
                    value={signatureRole}
                    onChange={(e) => setSignatureRole(e.target.value)}
                    placeholder="Fonction chez Horizon Solution"
                    className="h-9 text-sm"
                    style={{ borderColor: theme.border, color: theme.text, backgroundColor: theme.background }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-4 pt-1">
            {[
              {
                title: 'Général',
                items: [
                  ['include_cover', 'Page de titre (logo Horizon Solution) et période'],
                  ['include_exec_summary', 'Synthèse exécutive et indicateurs clés'],
                  ['include_alerts', 'Points d’attention'],
                ],
              },
              {
                title: 'Scan',
                items: [
                  ['include_scans_summary', 'Indicateurs agrégés'],
                  ['include_scans_by_day', 'Graphique + tableau par jour'],
                  ['include_scans_gps_stats', 'Précision GPS (agrégats)'],
                ],
              },
              {
                title: 'Contrôle qualité',
                items: [
                  ['include_quality_summary', 'Synthèse'],
                  ['include_quality_distribution', 'Graphique + répartition des notes'],
                  ['include_quality_by_form', 'Graphique + résultats par formulaire'],
                  ['include_weakest_criteria', 'Critères les plus faibles'],
                  ['include_validated_visits_table', 'Liste des visites validées'],
                ],
              },
            ].map((group) => (
              <div key={group.title} className="space-y-2">
                <p className="text-xs font-semibold" style={{ color: theme.accent }}>
                  {group.title}
                </p>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {group.items.map(([key, label]) => (
                    <label
                      key={key}
                      className="flex items-center gap-2 rounded-md border px-2.5 py-2 cursor-pointer text-sm"
                      style={{ borderColor: theme.border, color: theme.text }}
                    >
                      <Checkbox
                        checked={!!exportSections[key]}
                        onCheckedChange={() => toggleExportSection(key)}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              className="gap-1.5 rounded-md"
              style={{ backgroundColor: theme.accent, color: '#fff' }}
              disabled={!selectedEntrepriseId || exportingPdf || exportingXlsx}
              onClick={() => downloadExport('pdf')}
            >
              {exportingPdf ? (
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <FileDown className="h-3.5 w-3.5" />
              )}
              PDF
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="gap-1.5 rounded-md"
              style={{ borderColor: theme.border, color: theme.text }}
              disabled={!selectedEntrepriseId || exportingPdf || exportingXlsx}
              onClick={() => downloadExport('xlsx')}
            >
              {exportingXlsx ? (
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <FileDown className="h-3.5 w-3.5" />
              )}
              Excel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CompteRenduPage;
